/**
 * Example: ClientsPage with Real API Integration
 *
 * This file demonstrates how to connect your frontend to the Django backend.
 * Compare this with ClientsPage.js to see the differences.
 *
 * To use this:
 * 1. Rename ClientsPage.js to ClientsPage.mock.js (backup)
 * 2. Rename this file to ClientsPage.js
 * 3. Make sure backend is running on localhost:8000
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Button, Badge, LoadingSpinner } from './components';

function ClientsPage() {
  const navigate = useNavigate();
  // State
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('spent-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    address: '', city: '', zip_code: '', location_id: '',
    home_client: false, alf_client: false, event_client: false, notes: '',
    contact_name: '', contact_relationship: '', contact_phone: '',
  });
  const itemsPerPage = 10;

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const orderingMap = {
        'spent-desc': '-total_spent',
        'spent-asc': 'total_spent',
        'visits-desc': '-appointment_count',
        'visits-asc': 'appointment_count',
        'name-asc': 'person__first_name',
        'name-desc': '-person__first_name',
      };

      const params = {
        ordering: orderingMap[sortBy] || '-total_spent',
        page: currentPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (locationFilter && locationFilter !== 'all') {
        params.location = locationFilter;
      }

      const data = await apiService.getClients(params);
      setClients(data.results || data || []);
      setTotalCount(data.count || 0);

    } catch (err) {
      setError(err.message || 'Failed to load clients');
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, locationFilter, currentPage, sortBy]);

  // Load available locations
  const loadLocations = useCallback(async () => {
    try {
      const data = await apiService.getLocations();
      setLocations(data.results || data || []);
    } catch (err) {
      console.error('Error loading locations:', err);
      // Don't set error state for locations, just log it
    }
  }, []);

  const handleAddClient = async () => {
    if (!newClient.first_name.trim()) { alert('Please enter a first name'); return; }
    if (!newClient.phone.trim()) { alert('Please enter a phone number'); return; }
    setSaving(true);
    try {
      const result = await apiService.createClient(newClient);
      setShowAddModal(false);
      setNewClient({
        first_name: '', last_name: '', phone: '', email: '',
        address: '', city: '', zip_code: '', location_id: '',
        home_client: false, alf_client: false, event_client: false, notes: '',
        contact_name: '', contact_relationship: '', contact_phone: '',
      });
      loadClients();
      if (result?.id) navigate(`/clients/${result.id}`);
    } catch (err) {
      console.error('Error creating client:', err);
      alert('Failed to create client: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Fetch locations and clients from API on component mount
  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, locationFilter, sortBy]);

  // Filter clients locally (or pass to API)
  const filteredClients = clients.filter(client => {
    // Only show clients with at least 1 visit or revenue > $0
    const hasActivity = (client.appointment_count > 0) || (parseFloat(client.total_spent || 0) > 0);
    if (!hasActivity) return false;

    // Active = has future appointment OR last appointment within 60 days
    const isActive = client.has_future_appointment ||
      (client.last_appointment_date &&
       (new Date() - new Date(client.last_appointment_date)) / (1000 * 60 * 60 * 24) <= 60);

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && isActive) ||
                         (statusFilter === 'inactive' && !isActive);

    return matchesStatus;
  });

  // Server-side pagination — clients already sorted and paged by the API
  const currentClients = filteredClients;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Loading state
  if (loading && clients.length === 0) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <Panel className="bg-red-50 border-red-200">
            <h3 className="text-xs font-semibold text-red-800">Error Loading Clients</h3>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <Button onClick={loadClients} size="sm" className="mt-2 bg-red-600 hover:bg-red-700">
              Retry
            </Button>
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header */}
        <div className="mb-3 flex justify-between items-center">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Clients</h1>
            <p className="text-xs text-gray-600 mt-0.5">
              {totalCount} total clients
            </p>
          </div>
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Client
          </Button>
        </div>

        {/* Filters */}
        <Panel className="mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="col-span-1">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Locations</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="spent-desc">$ (High to Low)</option>
              <option value="spent-asc">$ (Low to High)</option>
              <option value="visits-desc">Visits (Most)</option>
              <option value="visits-asc">Visits (Least)</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
            </select>
          </div>
        </Panel>

        {/* Clients Table */}
        <Panel noPadding className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Client
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Contact
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Location
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Stats
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentClients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)}>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-900">
                      {client.person_name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-col gap-0.5">
                      {client.email && (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </div>
                      )}
                      {client.phone_number && (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Phone className="w-3 h-3" />
                          {client.phone_number}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-xs">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      {client.location_id ? (
                        <button
                          onClick={() => navigate(`/locations/${client.location_id}`)}
                          className="text-red-700 hover:text-red-900 hover:underline transition-colors"
                        >
                          {client.location_name}
                        </button>
                      ) : (
                        <span className="text-gray-600">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs">
                      <div className="text-gray-900 font-medium">
                        {client.appointment_count || 0} visits · {client.interaction_count || 0} interactions
                      </div>
                      <div className="text-gray-500">
                        ${client.total_spent || 0} spent
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {(() => {
                      // Active = has future appointment OR last appointment within 60 days
                      const isActive = client.has_future_appointment ||
                        (client.last_appointment_date &&
                         (new Date() - new Date(client.last_appointment_date)) / (1000 * 60 * 60 * 24) <= 60);
                      return (
                        <Badge variant={isActive ? 'success' : 'neutral'} small>
                          {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Panel>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-2 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span> of{' '}
                <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div className="flex gap-1">
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant={currentPage === 1 ? 'secondary' : 'primary'}
                size="sm"
              >
                Previous
              </Button>
              {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                const pageNum = currentPage <= 3 ? index + 1 : currentPage - 2 + index;
                if (pageNum > totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    variant={currentPage === pageNum ? 'primary' : 'secondary'}
                    size="sm"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                variant={currentPage === totalPages ? 'secondary' : 'primary'}
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-sm font-semibold text-gray-900">Add New Client</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">First Name *</label>
                  <input
                    type="text"
                    value={newClient.first_name}
                    onChange={e => setNewClient({...newClient, first_name: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Last Name</label>
                  <input
                    type="text"
                    value={newClient.last_name}
                    onChange={e => setNewClient({...newClient, last_name: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Phone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={e => setNewClient({...newClient, phone: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Email</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={e => setNewClient({...newClient, email: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Address</label>
                <input
                  type="text"
                  value={newClient.address}
                  onChange={e => setNewClient({...newClient, address: e.target.value})}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">City</label>
                  <input
                    type="text"
                    value={newClient.city}
                    onChange={e => setNewClient({...newClient, city: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Zip Code</label>
                  <input
                    type="text"
                    value={newClient.zip_code}
                    onChange={e => setNewClient({...newClient, zip_code: e.target.value})}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Location</label>
                <select
                  value={newClient.location_id}
                  onChange={e => setNewClient({...newClient, location_id: e.target.value})}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                >
                  <option value="">Select location...</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Client Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700">
                    <input type="checkbox" checked={newClient.home_client} onChange={e => setNewClient({...newClient, home_client: e.target.checked})} className="rounded" />
                    Home
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-700">
                    <input type="checkbox" checked={newClient.alf_client} onChange={e => setNewClient({...newClient, alf_client: e.target.checked})} className="rounded" />
                    ALF
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-700">
                    <input type="checkbox" checked={newClient.event_client} onChange={e => setNewClient({...newClient, event_client: e.target.checked})} className="rounded" />
                    Event
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Contact Person (caretaker, family member)</label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={newClient.contact_name}
                    onChange={e => setNewClient({...newClient, contact_name: e.target.value})}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                    placeholder="Name"
                  />
                  <select
                    value={newClient.contact_relationship}
                    onChange={e => setNewClient({...newClient, contact_relationship: e.target.value})}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                  >
                    <option value="">Relationship...</option>
                    <option value="family">Family</option>
                    <option value="caretaker">Caretaker</option>
                    <option value="facility">Facility Staff</option>
                    <option value="other">Other</option>
                  </select>
                  <input
                    type="tel"
                    value={newClient.contact_phone}
                    onChange={e => setNewClient({...newClient, contact_phone: e.target.value})}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
                    placeholder="Phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Notes</label>
                <textarea
                  value={newClient.notes}
                  onChange={e => setNewClient({...newClient, notes: e.target.value})}
                  rows={2}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none resize-none"
                  placeholder="Any notes about this client..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="secondary" size="sm" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddClient} disabled={saving || !newClient.first_name.trim()}>
                {saving ? 'Creating...' : 'Create Client'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsPage;
